/*
 * Eluxa phone-model auto-redirect.
 *
 * Meta ad clicks open in the Instagram / Facebook in-app browser, whose user agent exposes the phone's
 * device identifier (iPhone15,2) or Samsung model code (SM-S938B). This script turns that into a marketing
 * name ("iPhone 14 Pro") and sends the visitor to the matching product with the right variant selected.
 *
 * HOW TO MAINTAIN
 *  1. DEVICE TABLES below (IOS_MODELS, SAMSUNG_MODELS): device id -> marketing name. Add a line when a new
 *     phone launches. Names must match the store's "Model" variant titles (case-insensitive).
 *  2. PRODUCTS / VARIANTS / STOCK are NOT in this file. The "Eluxa model redirect" section (theme.liquid)
 *     reads them live from the products picked in its settings and passes them in as data-config, so a new
 *     model variant added in Shopify works automatically once its name is in the table above.
 *  3. On/off switch, landing collections and the iPhone/Samsung products are section settings in the theme
 *     editor (Customize -> "Eluxa model redirect").
 *
 * Verified sources for the tables: adamawolf iOS identifier list, iClarified IPSW pages (iPhone18,x),
 * Samsung retail listings / model-number guides (SM-S931, SM-S938, SM-S942, SM-S948).
 */
(function (root) {
  'use strict';

  // iPhone device identifier -> marketing name (iPhone 11 through iPhone 17 series).
  var IOS_MODELS = {
    'iPhone12,1': 'iPhone 11',
    'iPhone12,3': 'iPhone 11 Pro',
    'iPhone12,5': 'iPhone 11 Pro Max',
    'iPhone12,8': 'iPhone SE (2nd generation)',
    'iPhone13,1': 'iPhone 12 mini',
    'iPhone13,2': 'iPhone 12',
    'iPhone13,3': 'iPhone 12 Pro',
    'iPhone13,4': 'iPhone 12 Pro Max',
    'iPhone14,2': 'iPhone 13 Pro',
    'iPhone14,3': 'iPhone 13 Pro Max',
    'iPhone14,4': 'iPhone 13 mini',
    'iPhone14,5': 'iPhone 13',
    'iPhone14,6': 'iPhone SE (3rd generation)',
    'iPhone14,7': 'iPhone 14',
    'iPhone14,8': 'iPhone 14 Plus',
    'iPhone15,2': 'iPhone 14 Pro',
    'iPhone15,3': 'iPhone 14 Pro Max',
    'iPhone15,4': 'iPhone 15',
    'iPhone15,5': 'iPhone 15 Plus',
    'iPhone16,1': 'iPhone 15 Pro',
    'iPhone16,2': 'iPhone 15 Pro Max',
    'iPhone17,1': 'iPhone 16 Pro',
    'iPhone17,2': 'iPhone 16 Pro Max',
    'iPhone17,3': 'iPhone 16',
    'iPhone17,4': 'iPhone 16 Plus',
    'iPhone17,5': 'iPhone 16e',
    'iPhone18,1': 'iPhone 17 Pro',
    'iPhone18,2': 'iPhone 17 Pro Max',
    'iPhone18,3': 'iPhone 17',
    'iPhone18,4': 'iPhone Air',
    'iPhone18,5': 'iPhone 17e'
  };

  // Samsung base model code (regional suffix such as B / U / U1 / N / W is stripped) -> marketing name.
  var SAMSUNG_MODELS = {
    'S931': 'Samsung Galaxy S25',
    'S938': 'Samsung Galaxy S25 Ultra',
    'S942': 'Samsung Galaxy S26',
    'S948': 'Samsung Galaxy S26 Ultra'
  };

  // Which catalog family (see data-config.catalog) each platform's models live in.
  var FAMILY_FOR = { ios: 'iphone', android: 'samsung' };

  // Pages the redirect must never run on.
  var BLOCKED_PATHS = /^\/(products|cart|checkout|checkouts|account|search|password|blogs|policies)(\/|$)/;

  var IN_APP = /Instagram|FBAN|FBAV/;
  var AD_SOURCES = ['facebook', 'instagram', 'fb', 'ig', 'meta'];
  var SESSION_KEY = 'eluxaModelRedirected';

  function parseDevice(ua) {
    var ios = ua.match(/\b(iPhone\d{1,2},\d{1,2})\b/);
    if (ios) return { platform: 'ios', id: ios[1] };
    var sm = ua.match(/\bSM-([A-Z]\d{3})[A-Z0-9]*\b/);
    if (sm) return { platform: 'android', id: sm[1] };
    return null;
  }

  function marketingName(device) {
    if (!device) return null;
    if (device.platform === 'ios') return IOS_MODELS[device.id] || null;
    if (device.platform === 'android') return SAMSUNG_MODELS[device.id] || null;
    return null;
  }

  // Pure decision function. ctx: { ua, search, hash, pathname, config, alreadyRedirected }
  // Returns { action: 'redirect', url, model } or { action: 'none', reason }.
  function decide(ctx) {
    var cfg = ctx.config || {};
    if (!cfg.enabled) return { action: 'none', reason: 'disabled' };
    if (!IN_APP.test(ctx.ua || '')) return { action: 'none', reason: 'not an Instagram/Facebook in-app browser' };

    var params = new URLSearchParams(ctx.search || '');
    var source = (params.get('utm_source') || '').toLowerCase();
    var fromAd = AD_SOURCES.indexOf(source) !== -1 || params.has('fbclid');
    if (!fromAd) return { action: 'none', reason: 'not from a Meta ad' };

    var path = ctx.pathname || '/';
    if (BLOCKED_PATHS.test(path)) return { action: 'none', reason: 'blocked page (' + path + ')' };

    var handles = (cfg.collections || []).map(function (h) { return String(h).trim(); }).filter(Boolean);
    var m = path.match(/^\/collections\/([^\/]+)/);
    if (!m || handles.indexOf(m[1]) === -1) return { action: 'none', reason: 'not a configured landing collection' };

    if (ctx.alreadyRedirected) return { action: 'none', reason: 'already redirected this session' };

    var device = parseDevice(ctx.ua);
    var name = marketingName(device);
    if (!name) return { action: 'none', reason: device ? 'unknown model ' + device.id : 'no device id in user agent' };

    var family = (cfg.catalog || {})[FAMILY_FOR[device.platform]];
    var variant = family && family.variants && family.variants[name.toLowerCase()];
    if (!variant) return { action: 'none', reason: name + ' not in store catalog' };
    if (!variant.available) return { action: 'none', reason: name + ' out of stock' };

    params.set('variant', String(variant.id));
    return {
      action: 'redirect',
      model: name,
      url: family.url + '?' + params.toString() + (ctx.hash || '')
    };
  }

  function run() {
    var el = document.getElementById('eluxa-model-redirect');
    if (!el) return;
    var config;
    try { config = JSON.parse(el.getAttribute('data-config')); } catch (e) { return; }

    var already = false;
    try { already = sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) {}

    var result = decide({
      ua: navigator.userAgent,
      search: location.search,
      hash: location.hash,
      pathname: location.pathname,
      config: config,
      alreadyRedirected: already
    });

    if (result.action !== 'redirect') return;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
    location.replace(result.url);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { decide: decide, parseDevice: parseDevice, marketingName: marketingName, IOS_MODELS: IOS_MODELS, SAMSUNG_MODELS: SAMSUNG_MODELS };
  } else if (typeof document !== 'undefined') {
    run();
  }
})(this);
